/**
 * Worker-store reducer for view-level editing state: selection, cluster organization,
 * redaction settings, and background blur edits.
 *
 * Handles actions mirrored from the main thread (see src/utils/workeredux and
 * src/common-app/web-worker/actions) that select poly groups, rename/pin/merge/delete
 * clusters, reassign groups or individual segments between clusters, change shape,
 * redaction code, redaction config, or text-overlay content/style, and bulk-apply
 * per-detection-type redaction.
 * The text-overlay handler also enforces a position invariant: a pinned overlay must
 * not keep a `customRect` (see dropDeadCustomRect), so pinning a previously-dragged
 * overlay discards the position it was dragged to. The geometry writer in
 * reducers/overlay/udrs.ts and the load adapter both uphold the same rule.
 * Most handlers use immer produceWithPatches and push {patches, inversePatches} onto
 * state.history.past (clearing future) to power undo/redo; auto background blur is
 * added on selection when globalSettings.autoAddBackgroundBlur is on. Operates on the
 * WorkerState maps (clusterMap, groupMap, subsegmentMap, objectMap, udrAsset) defined
 * in src/common-app/web-worker/models.
 */
import { CaseReducer, Draft, createReducer, original } from '@reduxjs/toolkit';
import {
  UPDATE_CURRENT_TIME_MEDIA_PLAYER,
  CHANGE_CLUSTER_LABEL,
  SET_SELECTED_GROUPS,
  SetGlobalSettingsRequest,
  SetSelectedGroupsRequest,
  InChangeFilterParameters,
  ChangeShapeRequest,
  BulkRedactRequest,
  ChangeRedactionRequest,
  ChangeTextOverlayRequest,
  UPDATE_CLUSTER_ASSIGNMENT,
  CHANGE_CLUSTER_PINNED,
  DELETE_CLUSTER,
  CHANGE_CLUSTER_SEGMENT_SORT_TYPE,
  GroupIdAndType,
  SET_MERGE_SELECT_CLUSTER_IDS,
  SET_CLUSTER_MERGE_SEGMENTS,
  SET_MERGE_GROUP_ID,
  BulkAddBackgroundBlurRequest,
  REPLACE_SELECTED_GROUPS,
  REPLACE_CLUSTER_MAP,
  PURGE_DETECTIONS,
} from '@common-modules/mediaDetails/actions';

import {
  IN_CLEAR_MEDIA_DATA,
  IN_LOAD_MEDIA_DATA,
  IN_SET_GLOBAL_SETTINGS,
  OUT_FACE_DETECTION_READY,
  OUT_INTERPOLATION_UPDATE,
  IN_CHANGE_FILTER_PARAMETERS,
  CHANGE_SHAPE,
  SET_DETECTION_TYPE_REDACTION,
  SET_INDIVIDUAL_REDACTION,
  CHANGE_CODE,
  CHANGE_TEXT_OVERLAY,
  IN_SET_BACKGROUND_BLUR_EDITS,
  BULK_ADD_BACKGROUND_BLUR,
} from '../actions';
import {
  ClusterMapResponse,
  InLoadMediaRequest,
  SetBackgroundBlurEditsRequest,
  SubsegmentItem,
  WorkerState,
  namespace,
} from '../models';
import { defaultState } from '../store';
import {
  selectDetectionClusterGroups,
  selectGlobalSettings,
} from '../selectors';

import { adjustSelectedGroups } from '../helpers/GroupSelect';
import { produce, produceWithPatches } from 'immer';
import { isEqual, groupBy, sortBy, maxBy } from 'lodash';
import { v4 as uuid } from 'uuid';
import {
  ClusterSegment,
  BoundingPoly,
  RedactionConfig,
  ShapeType,
  TextOverlay,
  BlurLevel,
} from '@common/state/modules/mediaDetails/models';
import { IndividualRedactionCode } from '@redact/state/modules/redactionCodes/models';
import {
  CLUSTER_SEGMENT_SORT_TYPE,
  convertPolyToScalable,
  DEFAULT_BACKGROUND_BLUR_LEVEL,
  OBJECT_TYPE,
} from '@helpers/constants';
import {
  deleteDetectionGroupHelper,
  removeDetectionGroupFromClusterMapItem,
} from './overlay/heads';
import {
  deleteUdrGroupHelper,
  removeUdrGroupFromClusterMapItem,
} from './overlay/udrs';
import { moveManualGaptoNewGroup } from './overlay/gaps';
import { insertEditSlice } from '@common/state/modules/mediaDetails/reducers/background-blur';
import { BackgroundBlurSlice } from '@common/state/modules/mediaDetails/store.models';
import {
  getUDRGroupIntervalBasedSegments,
  selectDetectionClusterGroupsSubset,
} from '../selectors/clusters';
type Re<P = unknown> = CaseReducer<WorkerState, { payload: P; type: string }>;

const onSetGlobalSettings: Re<SetGlobalSettingsRequest> = (
  state,
  { payload }
) => {
  const [nextState, patches, inversePatches] = produceWithPatches(
    state,
    (draft) => {
      if (draft.tdo && !isEqual(draft.tdo.details.settings, payload)) {
        Object.assign(draft.tdo.details.settings, payload);
      }
    }
  );

  return produce(nextState, (draft) => {
    if (patches.length && inversePatches.length) {
      draft.history.past.push({ patches, inversePatches });
      draft.history.future = [];
    }
  });
};

const onSetSelectedGroups: Re<SetSelectedGroupsRequest> = (state, action) => {
  const description = action.payload.description;

  const [nextState, patches, inversePatches] = produceWithPatches(
    state,
    (draft) => {
      draft.selectedPolyGroups = {
        ...draft.selectedPolyGroups,
        ...action.payload.selected,
      };

      const globalSettings = selectGlobalSettings({
        [namespace]: draft,
      });

      // auto add background blur on cluster/group/segment selection

      // if not automatically add background blur done
      if (!globalSettings.autoAddBackgroundBlur) {
        return;
      }

      const udrGroupIds: string[] = [];
      const detectionGroupIds: string[] = [];

      if (action.payload.selected) {
        // find detection groups
        Object.entries(action.payload.selected).forEach(
          ([subsegmentId, selected]) => {
            if (selected) {
              // check if is a detection
              if (draft.subsegmentMap[subsegmentId]) {
                const subsegment = draft.subsegmentMap[subsegmentId];
                const groupId = subsegment.groupId;
                const type = convertPolyToScalable(
                  draft.groupMap[groupId]?.type
                );

                // check if group is using the default inverse blur effect
                if (
                  type &&
                  globalSettings.objectTypeEffects[type].redactionConfig
                    .fillType === 'inverse_blur' &&
                  !draft.udrAsset.boundingPolys[groupId]?.redactionConfig
                ) {
                  detectionGroupIds.push(subsegment.groupId);
                } else if (
                  draft.udrAsset.boundingPolys[groupId]?.redactionConfig
                    ?.addBackgroundBlur
                ) {
                  // or if group uses custom inverse blur + background blur
                  detectionGroupIds.push(subsegment.groupId);
                }
              }
            }
          }
        );
      }

      // find udr groups
      if (action.payload.groupIds) {
        // if any of the selected groups use inverse blur create background blur by default

        action.payload.groupIds.forEach((groupId) => {
          if (draft.udrAsset.boundingPolys[groupId]) {
            // check if group is using the default inverse blur effect
            if (
              globalSettings.objectTypeEffects['udr'].redactionConfig
                .fillType === 'inverse_blur' &&
              !draft.udrAsset.boundingPolys[groupId].redactionConfig
            ) {
              udrGroupIds.push(groupId);
            } else if (
              // or if group uses custom inverse blur + background blur
              draft.udrAsset.boundingPolys[groupId].redactionConfig
                ?.addBackgroundBlur
            ) {
              udrGroupIds.push(groupId);
            }
          }
        });
      }

      const backgroundBlurLevel =
        globalSettings.defaultBackgroundBlurLevel ||
        DEFAULT_BACKGROUND_BLUR_LEVEL;

      if (backgroundBlurLevel) {
        addBackgroundBlurEdits(
          draft,
          udrGroupIds,
          detectionGroupIds,
          backgroundBlurLevel
        );
      }
    }
  );

  return produce(nextState, (draft) => {
    draft.history.past.push({
      patches,
      inversePatches,
      metadata: { description },
    });
    draft.history.future = [];
  });
};

const onReplaceSelectedGroups: Re<SetSelectedGroupsRequest> = (
  state,
  action
) => {
  const [nextState, patches, inversePatches] = produceWithPatches(
    state,
    (draft) => {
      draft.selectedPolyGroups = action.payload.selected;
    }
  );

  return produce(nextState, (draft) => {
    draft.history.past.push({ patches, inversePatches });
    draft.history.future = [];
  });
};

const onReplaceClusterGroups: Re<ClusterMapResponse> = (state, action) => {
  const [nextState, patches, inversePatches] = produceWithPatches(
    state,
    (draft) => {
      draft.clusterMap = action.payload;
    }
  );

  return produce(nextState, (draft) => {
    draft.history.past.push({ patches, inversePatches });
    draft.history.future = [];
  });
};

const onPurgeDetections: Re<undefined> = (state) => {
  const [nextState, patches, inversePatches] = produceWithPatches(
    state,
    (draft) => {
      draft.groupMap = {};
      draft.subsegmentMap = {};
      draft.objectMap = {};
    }
  );

  return produce(nextState, (draft) => {
    draft.history.past.push({ patches, inversePatches });
    draft.history.future = [];
  });
};

const onDeleteCluster = (
  state: Draft<WorkerState>,
  action: {
    payload: {
      clusterId: string;
      udrGroupIds: string[];
      detectionGroups: GroupIdAndType[];
    };
  }
) => {
  const [nextState, patches, inversePatches] = produceWithPatches(
    state,
    (draft) => {
      // remove from clusterMap
      const clusterId = action.payload.clusterId;
      const cluster = state.clusterMap[clusterId];
      if (cluster) {
        delete draft.clusterMap[clusterId];
      }

      for (const groupId of action.payload.udrGroupIds) {
        const boundingPolysGroup = state.udrAsset.boundingPolys[groupId];
        deleteUdrGroupHelper(draft, groupId, boundingPolysGroup);
      }

      for (const { id: groupId } of action.payload.detectionGroups) {
        deleteDetectionGroupHelper(draft, state, groupId);
      }
    }
  );

  return produce(nextState, (draft) => {
    draft.history.past.push({ patches, inversePatches });
    draft.history.future = [];
  });
};

const onSetChangedClusterLabels = (
  state: Draft<WorkerState>,
  action: {
    payload: {
      clusterId: string;
      type: string;
      userLabel: string;
    };
  }
) => {
  changeClusterLabel(state, action.payload);
};

const changeClusterLabel = (
  state: Draft<WorkerState>,
  payload: {
    clusterId: string;
    type: string;
    userLabel: string;
  }
) => {
  let groupId: string;

  // check if clusterId is for a cluster
  const clusterMapItem = state.clusterMap[payload.clusterId];
  if (clusterMapItem) {
    clusterMapItem.userLabel = payload.userLabel;

    // if cluster only has 1 group update the group label as well
    if (
      clusterMapItem.udrGroupIds.length === 1 &&
      clusterMapItem.detectionGroupIds.length === 0
    ) {
      groupId = clusterMapItem.udrGroupIds[0]!; // check is above
    } else if (
      clusterMapItem.udrGroupIds.length === 0 &&
      clusterMapItem.detectionGroupIds.length === 1
    ) {
      groupId = clusterMapItem.detectionGroupIds[0]!; // check is above
    } else {
      return; // nothing left to do
    }
  } else {
    // otherwise clusterId is actually a groupId
    groupId = payload.clusterId;
  }

  const { groupMap, udrAsset } = state;
  //  * if type = udr, change udrAsset and return
  if (payload.type === 'udr') {
    const udrGroup = udrAsset.boundingPolys[groupId];
    if (udrGroup) {
      udrGroup.userLabel = payload.userLabel;
    }
    return;
  }

  // * if type = detection, update groupMap
  const group = groupMap[groupId];
  if (group) {
    group.userLabel = payload.userLabel;
    return;
  }
};

const onSetChangedSegmentSortType = (
  state: Draft<WorkerState>,
  action: {
    payload: {
      clusterId: string;
      segmentSortType: CLUSTER_SEGMENT_SORT_TYPE;
    };
  }
) => {
  changeSegmentSortType(state, action.payload);
};

const changeSegmentSortType = (
  state: Draft<WorkerState>,
  payload: {
    clusterId: string;
    segmentSortType: CLUSTER_SEGMENT_SORT_TYPE;
  }
) => {
  // This function is only for multi-group clusters, with type === 'mixed',
  // so cluster should be exists
  const cluster = state.clusterMap[payload.clusterId];

  if (cluster) {
    cluster.sortBy = payload.segmentSortType;
  }
};

const onSetChangedClusterPinned = (
  state: Draft<WorkerState>,
  action: {
    payload: {
      clusterId: string;
      type: string;
      pinnedDate: number | undefined;
      generatedId: string;
    };
  }
) => {
  changeClusterPinned(state, action.payload);
};

const changeClusterPinned = (
  state: Draft<WorkerState>,
  payload: {
    clusterId: string;
    pinnedDate: number | undefined;
    generatedId: string;
  }
) => {
  // check if clusterId is for a multi-group cluster
  const cluster = state.clusterMap[payload.clusterId];
  if (cluster) {
    cluster.pinnedDate = payload.pinnedDate;
    return;
  }

  // must be a groupId - need to create cluster in order to pin
  const groupId = payload.clusterId;
  const newClusterId = payload.generatedId;
  const { groupMap, udrAsset } = state;

  const udrGroupIds = [];
  const detectionGroupIds = [];
  let userLabel: string | undefined;

  // if group type is udr
  if (udrAsset.boundingPolys[groupId]) {
    udrAsset.boundingPolys[groupId] = {
      ...udrAsset.boundingPolys[groupId],
      clusterId: newClusterId,
    };
    userLabel = udrAsset.boundingPolys[groupId].userLabel;
    udrGroupIds.push(groupId);
  } else if (groupMap[groupId]) {
    groupMap[groupId] = {
      ...groupMap[groupId],
      clusterId: newClusterId,
    };
    userLabel = groupMap[groupId].userLabel;
    detectionGroupIds.push(groupId);
  } else {
    // something is wrong
    return;
  }

  // finally add new entry to clusterMap
  state.clusterMap = {
    ...state.clusterMap,
    [newClusterId]: {
      id: newClusterId,
      pinnedDate: payload.pinnedDate,
      udrGroupIds,
      detectionGroupIds,
      userLabel, // copy over group userLabel
    },
  };
};

const getPinnedDate = (draft: Draft<WorkerState>, groupIds: string[]) => {
  // take most recent pinned date from one of the source groups/clusters
  const { groupMap, udrAsset } = draft;
  const pinnedDates: number[] = [];
  for (const groupId of groupIds) {
    let parentClusterId;

    // check if is a udr group
    const udrGroup = udrAsset.boundingPolys[groupId];
    if (udrGroup) {
      parentClusterId = udrGroup.clusterId;
    }
    // check if is detection group
    const detectionGroup = groupMap[groupId];
    if (detectionGroup) {
      parentClusterId = detectionGroup.clusterId;
    }
    // if assigned to to cluster use that userLabel instead
    if (parentClusterId) {
      const sourceCluster = draft.clusterMap[parentClusterId];
      if (sourceCluster && sourceCluster.pinnedDate) {
        pinnedDates.push(sourceCluster.pinnedDate);
      }
    }
  }
  if (pinnedDates.length > 0) {
    return Math.max(...pinnedDates);
  }
  return undefined;
};

const onUpdateClusterAssignment = (
  state: Draft<WorkerState>,
  action: {
    payload: {
      groupIds?: string[] | undefined;
      segments?: ClusterSegment[] | undefined;
      assignedClusterId: string | undefined;
      clusterUserLabel?: string;
    };
  }
) => {
  const [nextState, patches, inversePatches] = produceWithPatches(
    state,
    (draft) => {
      const { groupMap, udrAsset } = draft;

      draft.lastUdrGroupsWithUpdatedClusterAssignment = [];
      draft.lastDetectionGroupsWithUpdatedClusterAssignment = [];
      draft.lastAssignedClusterId = undefined;

      const groupIds = action.payload.groupIds || [];

      // sometimes assignedClusterId is groupId
      let assignedClusterId = action.payload.assignedClusterId;
      if (
        assignedClusterId &&
        (udrAsset.boundingPolys[assignedClusterId] ||
          groupMap[assignedClusterId])
      ) {
        groupIds.push(assignedClusterId);
      }

      const clusterUserLabel = action.payload.clusterUserLabel;

      // if merging any groups then assign to the biggest cluster any groups belong to
      if (groupIds.length > 0) {
        // get list of unique clusterIds
        const clusterIds = new Set(
          groupIds.map((groupId) => {
            const udrGroup = udrAsset.boundingPolys[groupId];
            if (udrGroup) {
              return udrGroup.clusterId;
            }

            const detectionGroup = groupMap[groupId];
            if (detectionGroup) {
              return detectionGroup.clusterId;
            }
          })
        );

        // pick clusterId with the most groups
        let maxClusterId = uuid();
        let maxClusterIdGroupCount = 0;
        clusterIds.forEach((clusterId) => {
          if (!clusterId) {
            return;
          }
          const cluster = draft.clusterMap[clusterId];
          if (!cluster) {
            return;
          }
          const groupCount =
            cluster.detectionGroupIds.length + cluster.udrGroupIds.length;
          if (groupCount > maxClusterIdGroupCount) {
            maxClusterId = clusterId!;
            maxClusterIdGroupCount = groupCount;
          }
        });

        assignedClusterId = maxClusterId;
      }

      if (!assignedClusterId) {
        assignedClusterId = uuid();
      }

      const pinnedDate = getPinnedDate(draft, groupIds);

      let clusterMapItem = draft.clusterMap[assignedClusterId];
      if (clusterMapItem) {
        // updating existing cluster
        if (clusterUserLabel) {
          clusterMapItem.userLabel = clusterUserLabel;
        }
        clusterMapItem.pinnedDate = pinnedDate;
      } else {
        // add new cluster to clusterMap
        clusterMapItem = {
          id: assignedClusterId,
          detectionGroupIds: [],
          udrGroupIds: [],
          userLabel: clusterUserLabel,
          ...(pinnedDate && { pinnedDate }),
        };
        draft.clusterMap[assignedClusterId] = clusterMapItem;
      }

      // -------------------------
      // reassign entire groups
      // -------------------------

      if (!clusterMapItem) {
        return; // this shouldn't happen
      }

      for (const groupId of groupIds) {
        // check if is a udr group
        const udrGroup = udrAsset.boundingPolys[groupId];
        if (udrGroup) {
          // if group already in cluster continue
          if (udrGroup.clusterId === assignedClusterId) {
            continue;
          }

          if (!clusterMapItem.udrGroupIds.includes(groupId)) {
            // extra safe make sure not already there
            clusterMapItem.udrGroupIds.push(groupId); // update clustermap - add new assignment
          }
          draft.lastUdrGroupsWithUpdatedClusterAssignment.push(groupId);

          // check for change in cluster assignment
          if (udrGroup.clusterId !== assignedClusterId) {
            // remove from old cluster
            removeUdrGroupFromClusterMapItem(
              draft,
              udrGroup.clusterId,
              groupId
            );

            udrGroup.clusterId = assignedClusterId; // update cluster assignment
          }

          continue;
        }

        // check if is a detection group
        const detectionGroup = groupMap[groupId];
        if (detectionGroup) {
          // if group already in cluster continue
          if (detectionGroup.clusterId === assignedClusterId) {
            continue;
          }

          if (!clusterMapItem.detectionGroupIds.includes(groupId)) {
            // extra safe make sure not already there
            clusterMapItem.detectionGroupIds.push(groupId); // update clustermap - add new assignment
          }

          draft.lastDetectionGroupsWithUpdatedClusterAssignment.push(groupId);
          // check for change in cluster assignment
          if (detectionGroup.clusterId !== assignedClusterId) {
            // remove from old cluster
            removeDetectionGroupFromClusterMapItem(
              draft,
              detectionGroup.clusterId,
              groupId
            );

            detectionGroup.clusterId = assignedClusterId; // update cluster assignment
          }
        }
      }

      // -------------------------
      // reassign individual segments - this is much more complicated
      // -------------------------

      // group by groupId
      const segmentsByGroup = groupBy(
        action.payload.segments,
        ({ groupId }) => groupId
      );
      if (!segmentsByGroup) {
        return;
      }

      for (const groupId of Object.keys(segmentsByGroup)) {
        const segments = segmentsByGroup[groupId]!;

        // create new groupId to move segments to
        let newGroupId;

        if (groupMap[groupId]) {
          // must be detection when in the groupMap
          const origGroupItem = groupMap[groupId];

          // create new "label" since still use it during loading
          const newLabel = uuid();
          newGroupId = groupMap[groupId].type + '_' + newLabel;

          draft.lastDetectionGroupsWithUpdatedClusterAssignment.push(groupId);
          draft.lastDetectionGroupsWithUpdatedClusterAssignment.push(
            newGroupId
          );

          // add new group
          if (!clusterMapItem.detectionGroupIds) {
            clusterMapItem.detectionGroupIds = [newGroupId];
          } else {
            clusterMapItem.detectionGroupIds.push(newGroupId);
          }

          const allGroupSubsegmentIds = [];
          const newGroupSeries: BoundingPoly[] = [];

          for (const segment of segments) {
            // special handling for manual segments
            if (segment.isManualInterpolation) {
              // if is a manual gap need to create a new subsegment
              const newSubsegment = moveManualGaptoNewGroup(
                draft,
                segment.subsegmentIds[0]!, // manual segments only will have 1 subsegment
                groupId,
                newGroupId
              );

              if (!newSubsegment) {
                // something went wrong
                continue;
              }

              // promote to an actual subsegment
              draft.subsegmentMap[newSubsegment.id] = newSubsegment;

              allGroupSubsegmentIds.push(newSubsegment.id);
            } else {
              // add to new group

              for (const subsegmentId of segment.subsegmentIds) {
                // remove subsegmentId from original group - maybe a more efficient way to implement this
                const index = origGroupItem.subsegmentIds.indexOf(subsegmentId); // remove all subsegments from current group
                if (index >= 0) {
                  origGroupItem.subsegmentIds.splice(index, 1);
                }

                // create new subsegment id
                let newSubsegmentId;

                // update groupId for subsegments
                const subsegment = draft.subsegmentMap[subsegmentId];
                if (subsegment) {
                  subsegment.groupId = newGroupId;

                  newSubsegmentId = `${subsegment.startTimeMs}-${subsegment.stopTimeMs}-${newGroupId}`;
                  subsegment.id = newSubsegmentId; // update subsegment

                  // replace subsegment with new one with updated id and groupId
                  draft.subsegmentMap[newSubsegmentId] = subsegment;

                  // not necessary but if renamed objectIds might be cleaner

                  delete draft.subsegmentMap[subsegmentId];
                } else {
                  // if missing must be an auto interpolation gap
                  const gaps = draft.detectionGaps[segment.groupId];
                  if (gaps) {
                    // find gap with subsegmentId
                    const gap = Object.values(gaps).find(
                      (gap) => gap.subsegmentId === subsegmentId
                    );
                    if (gap) {
                      // move gap to the new group
                      gap.groupId = newGroupId;
                      gap.label = newLabel;

                      // create new subsegment id
                      newSubsegmentId = `${gap.leftObject.stopTimeMs}-${gap.rightObject.startTimeMs}-${newGroupId}`;
                      gap.subsegmentId = newSubsegmentId;

                      if (!draft.detectionGaps[newGroupId]) {
                        draft.detectionGaps[newGroupId] = {};
                      }
                      draft.detectionGaps[newGroupId]![gap.id] = gap; // ! creates default line above

                      // delete from old group
                      delete gaps[gap.id];
                    }
                  } else {
                    continue;
                  }
                }

                if (newSubsegmentId) {
                  allGroupSubsegmentIds.push(newSubsegmentId);
                  if (draft.selectedPolyGroups[subsegmentId] !== undefined) {
                    draft.selectedPolyGroups[newSubsegmentId] =
                      draft.selectedPolyGroups[subsegmentId];
                    delete draft.selectedPolyGroups[subsegmentId];
                  }
                }
              }
            }

            // collect all objects - also update label
            for (const subId of allGroupSubsegmentIds) {
              for (const objectId of draft.subsegmentMap[subId]?.objectIds ||
                []) {
                if (draft.objectMap[objectId]) {
                  // CTODO would like to get rid of label on object!
                  draft.objectMap[objectId].object.label = newLabel;
                  newGroupSeries.push(draft.objectMap[objectId]);
                }
              }
            }
          }

          let picUri = '';
          let maxItem = maxBy(newGroupSeries, (pbp) => pbp.object?.confidence);
          if (maxItem) {
            // construct media-streamer uri
            picUri =
              `${draft.apiRoot}/media-streamer/image/${
                draft.tdo!.id
              }?offsetMs=${maxItem.startTimeMs.toString()}&` +
              maxItem.object?.boundingPoly
                .map(({ x, y }, j) => `x[${j}]=${x}&y[${j}]=${y}`)
                .join('&');
          }

          // create new groupMapItem
          groupMap[newGroupId] = {
            ...origGroupItem,
            id: newGroupId,
            clusterId: assignedClusterId,
            subsegmentIds: sortSubsegmentIdsByTime(
              // sort by time CTODO investigate if this is necessary
              draft,
              allGroupSubsegmentIds
            ),
            picUri,
            label: newLabel,
            version: 0,
          };

          // update picUri on orig group
          const origGroupSeries: BoundingPoly[] = [];
          for (const subsegmentId of origGroupItem.subsegmentIds) {
            for (const objectId of draft.subsegmentMap[subsegmentId]
              ?.objectIds || []) {
              if (draft.objectMap[objectId]) {
                origGroupSeries.push(draft.objectMap[objectId]);
              }
            }
          }

          if (origGroupSeries.length) {
            maxItem = maxBy(origGroupSeries, (pbp) => pbp.object?.confidence);
            if (maxItem) {
              groupMap[groupId].picUri =
                `${draft.apiRoot}/media-streamer/image/${
                  draft.tdo!.id
                }?offsetMs=${maxItem.startTimeMs.toString()}&` +
                maxItem.object?.boundingPoly
                  .map(({ x, y }, j) => `x[${j}]=${x}&y[${j}]=${y}`)
                  .join('&');
            }
          }

          // if origGroup was completely reassigned need to remove
          if (origGroupSeries.length === 0) {
            // remove from old cluster
            removeDetectionGroupFromClusterMapItem(
              draft,
              origGroupItem.clusterId,
              origGroupItem.id
            );
            delete groupMap[origGroupItem.id];
          }
        } else {
          // udr group - data is stored in different format

          newGroupId = uuid();
          draft.lastUdrGroupsWithUpdatedClusterAssignment.push(newGroupId);
          draft.lastUdrGroupsWithUpdatedClusterAssignment.push(groupId);

          // add new group
          if (!clusterMapItem.udrGroupIds) {
            clusterMapItem.udrGroupIds = [newGroupId];
          } else {
            clusterMapItem.udrGroupIds.push(newGroupId);
          }

          const origUdrGroup = udrAsset.boundingPolys[groupId];
          if (!origUdrGroup) {
            continue;
          }

          const newGroupSeries = [];
          const remainGroupSeries = [];
          const objectIdSet = new Set(segments.flatMap((s) => s.objectIds));

          for (const seriesItem of origUdrGroup.series) {
            if (objectIdSet.has(seriesItem.id)) {
              newGroupSeries.push(seriesItem);
            } else {
              remainGroupSeries.push(seriesItem);
            }
          }
          origUdrGroup.series = remainGroupSeries;

          // create new UDRsPolyAssetGroup
          const sortedNewGroupSeries = sortBy(newGroupSeries, 'startTimeMs');

          udrAsset.boundingPolys[newGroupId] = {
            ...origUdrGroup,
            userLabel: clusterUserLabel || '',
            groupId: newGroupId,
            clusterId: assignedClusterId,
            series: sortedNewGroupSeries,
            version: 0,
            // would like to eliminate basePoly!
          };

          // if origUdrGroup was completely reassigned need to remove
          if (origUdrGroup.series.length === 0) {
            // remove from old cluster
            removeUdrGroupFromClusterMapItem(
              draft,
              origUdrGroup.clusterId,
              origUdrGroup.groupId
            );
            udrAsset.boundingPolys[origUdrGroup.groupId] = null;
          }
        }
      }

      // save list of all groups that will need to be updated in the outbound.ts
      draft.lastAssignedClusterId = assignedClusterId;
    }
  );
  return produce(nextState, (draft) => {
    draft.history.past.push({ patches, inversePatches });
    draft.history.future = [];
  });
};

const sortSubsegmentIdsByTime = (state: WorkerState, subsegmentIds: string[]) =>
  subsegmentIds
    .reduce<SubsegmentItem[]>((result, id) => {
      const subsegment = state.subsegmentMap[id];
      if (subsegment) {
        result.push(subsegment);
      }
      return result;
    }, [])
    .sort((a, b) => a.startTimeMs - b.startTimeMs)
    .map((subsegment) => subsegment.id);

const onUpdateCurrentTime: Re<{ currentPosition: number }> = (
  state,
  action
) => ({
  ...state,
  currentPosition: action.payload.currentPosition,
});

const onInLoadMedia: Re<InLoadMediaRequest> = (state, { payload }) => ({
  ...defaultState,
  // `tdo` is deliberately the OLD one until IN_SET_TDO lands; `currentTdoId`
  // is the media being loaded right now, which is what late responses are
  // tested against.
  tdo: state.tdo,
  currentTdoId: payload.tdoId,
  mediaDuration: payload.mediaDuration,
  downloadingJobs: state.downloadingJobs,
});

const onClearMediaData = (state: WorkerState) => ({
  ...defaultState,
  downloadingJobs: state.downloadingJobs,
});

const onFaceDetectionReady: Re<void> = (state) => adjustSelection(state);

const onOutInterpolationUpdate: Re<void> = (state) => adjustSelection(state);

function adjustSelection(state: WorkerState): WorkerState {
  const detectionClusterGroups = selectDetectionClusterGroups({
    [namespace]: state,
  });

  if (!detectionClusterGroups) {
    return state;
  }

  const selectedPolyGroups = adjustSelectedGroups(
    state.selectedPolyGroups,
    detectionClusterGroups
  );

  return {
    ...state,
    selectedPolyGroups,
  };
}

const onSetFilterParameters = (
  state: WorkerState,
  { payload }: { payload: InChangeFilterParameters }
) => {
  if (payload.preventUndo) {
    // sometimes don't want to allow undo/redo - for example when drawing new udr that modifies filter it is difficult to undo both actions in one undo step
    return {
      ...state,
      filterParameters: payload.filterParameters,
    };
  } else {
    const [nextState, patches, inversePatches] = produceWithPatches(
      state,
      (draft) => {
        draft.filterParameters = payload.filterParameters;
      }
    );

    return produce(nextState, (draft) => {
      draft.history.past.push({ patches, inversePatches });
      draft.history.future = [];
    });
  }
};

/**
const onSetFilterListRemove = (
  state: WorkerState,
  { payload }: { payload: InChangeFiltersRequest }
) => ({
  ...state,
  filterListRemove: payload.filterList,
});

const onSetFilterListShow = (
  state: WorkerState,
  { payload }: { payload: InChangeFiltersRequest }
) => {
  const [nextState, patches, inversePatches] = produceWithPatches(
    state,
    (draft) => {
      draft.filterListShow = payload.filterList;
    }
  );

  return produce(nextState, (draft) => {
    const reverseIndex = [...draft.history.past]
      .reverse()
      .findIndex(
        (past) =>
          past &&
          'patches' in past &&
          past.patches.some((patch) =>
            patch.path.includes('selectedPolyGroups')
          )
      );
    let selectedPolyGroupsUndo = undefined;
    if (reverseIndex !== -1) {
      const index = draft.history.past.length - reverseIndex - 1;
      selectedPolyGroupsUndo = draft.history.past.splice(index, 1)?.[0];
    }

    draft.history.past.push({
      patches: [...(selectedPolyGroupsUndo?.patches ?? []), ...patches],
      inversePatches: [
        ...(selectedPolyGroupsUndo?.inversePatches ?? []),
        ...inversePatches,
      ],
    });
    draft.history.future = [];
  });
};
*/

const updateGroupShape = ({
  draft,
  type,
  id,
  groupId,
  shapeType,
}: {
  draft: Draft<WorkerState>;
  type: OBJECT_TYPE;
  id: string;
  groupId: string;
  shapeType?: ShapeType;
}) => {
  if (type === 'udr') {
    const group = draft.udrAsset.boundingPolys[groupId];
    if (group) {
      draft.udrAsset.boundingPolys = {
        ...draft.udrAsset.boundingPolys,
        [groupId]: {
          ...group,
          shapeType,
          version: (group.version ?? 0) + 1,
        },
      };
    }
    return draft;
  }

  if (groupId) {
    const objectMap = draft.objectMap;

    const cluster = draft.groupMap[groupId];
    if (!cluster) {
      return;
    }

    cluster.shapeType = shapeType;

    for (const subsegmentId of original(draft)?.groupMap[groupId]
      ?.subsegmentIds || []) {
      for (const objectId of original(draft)?.subsegmentMap[subsegmentId]
        ?.objectIds || []) {
        const res = objectMap[objectId];
        if (res) {
          res.shapeType = shapeType;
          res.version = (res.version ?? 0) + 1;
        }
      }
    }
    return;
  }

  const res = draft.objectMap[id];
  if (res) {
    res.shapeType = shapeType;
    res.version = (res.version ?? 0) + 1;
  }
  return;
};

const onChangeShape = (
  state: Draft<WorkerState>,
  { payload }: { payload: ChangeShapeRequest }
) => {
  const { id, shapeType, udrGroupIds, detectionGroups } = payload;
  const [newState, patches, inversePatches] = produceWithPatches(
    state,
    (draft) => {
      udrGroupIds.forEach((groupId) =>
        updateGroupShape({
          draft,
          type: 'udr',
          id,
          groupId,
          shapeType,
        })
      );
      detectionGroups.forEach(({ id: groupId, type: groupType }) =>
        updateGroupShape({
          draft,
          type: groupType,
          id,
          groupId,
          shapeType,
        })
      );
    }
  );

  return produce(newState, (draft) => {
    draft.history.past.push({
      patches,
      inversePatches,
    });
    draft.history.future = [];
  });
};

const updateGroupCode = ({
  draft,
  type,
  groupId,
  redactionCode,
}: {
  draft: Draft<WorkerState>;
  type: OBJECT_TYPE;
  groupId: string;
  redactionCode?: IndividualRedactionCode;
}) => {
  if (type === 'udr') {
    const group = draft.udrAsset.boundingPolys[groupId];
    if (group) {
      group.redactionCode = redactionCode;
      group.version = (group.version ?? 0) + 1;
    }
    return;
  } else {
    // a detection
    const group = draft.groupMap[groupId];
    if (!group) {
      return;
    }

    group.redactionCode = redactionCode;
    for (const subsegmentId of group.subsegmentIds) {
      for (const objectId of original(draft)?.subsegmentMap[subsegmentId]
        ?.objectIds || []) {
        const res = draft.objectMap[objectId];

        if (res) {
          res.redactionCode = redactionCode;
          res.version = (res.version ?? 0) + 1;
        }
      }
    }
    return;
  }
};

const onChangeCode = (
  state: Draft<WorkerState>,
  { payload }: ReturnType<typeof CHANGE_CODE>
) => {
  const { redactionCode, udrGroupIds, detectionGroups } = payload;
  const [newState, patches, inversePatches] = produceWithPatches(
    state,
    (draft) => {
      detectionGroups.forEach(({ id: groupId, type }) =>
        updateGroupCode({
          draft,
          type,
          groupId,
          redactionCode,
        })
      );
      udrGroupIds.forEach((groupId) =>
        updateGroupCode({
          draft,
          type: 'udr',
          groupId,
          redactionCode,
        })
      );
    }
  );

  return produce(newState, (draft) => {
    draft.history.past.push({ patches, inversePatches });
    draft.history.future = [];
  });
};

/**
 * A pinned overlay's position is owned by its screenPosition* pins, so it must
 * not also carry a `customRect` — that would be derived state in an authored
 * field, dead while pinned and liable to resurface as the box if the pins were
 * later cleared. Pinning a previously-dragged overlay therefore drops the rect
 * it was dragged to. Mirrors applyGeometryWrite (which only writes customRect
 * when unpinned) and the load adapter (which only hoists one when unpinned),
 * so all three agree on the invariant: pinned XOR customRect (VE-30942).
 */
const dropDeadCustomRect = (textOverlay?: TextOverlay) => {
  if (
    !textOverlay?.customRect ||
    (!textOverlay.screenPositionHorizontal &&
      !textOverlay.screenPositionVertical)
  ) {
    return textOverlay;
  }
  const { customRect: _dropped, ...rest } = textOverlay;
  return rest;
};

const updateGroupTextOverlay = ({
  draft,
  type,
  groupId,
  textOverlay,
}: {
  draft: Draft<WorkerState>;
  type: OBJECT_TYPE;
  groupId: string;
  textOverlay?: TextOverlay;
}) => {
  if (type === 'udr') {
    const group = draft.udrAsset.boundingPolys[groupId];
    if (group) {
      group.textOverlay = dropDeadCustomRect(textOverlay);
      group.version = (group.version ?? 0) + 1;
    }
    return;
  }

  const group = draft.groupMap[groupId];
  if (!group) {
    return;
  }

  for (const subsegmentId of group.subsegmentIds) {
    for (const objectId of original(draft)?.subsegmentMap[subsegmentId]
      ?.objectIds || []) {
      const res = draft.objectMap[objectId];
      if (res) {
        res.textOverlay = textOverlay;
        res.version = (res.version ?? 0) + 1;
      }
    }
  }
};

const onChangeTextOverlay = (
  state: Draft<WorkerState>,
  { payload }: { payload: ChangeTextOverlayRequest }
) => {
  const { textOverlay, udrGroupIds, detectionGroups } = payload;
  const [newState, patches, inversePatches] = produceWithPatches(
    state,
    (draft) => {
      detectionGroups.forEach(({ id: groupId, type }) =>
        updateGroupTextOverlay({
          draft,
          type,
          groupId,
          textOverlay,
        })
      );
      udrGroupIds.forEach((groupId) =>
        updateGroupTextOverlay({
          draft,
          type: 'udr',
          groupId,
          textOverlay,
        })
      );
    }
  );

  // Nothing changed (e.g. every target group was already deleted — a panel
  // close-flush can race a delete): don't record a no-op undo step, which
  // would make the next Ctrl+Z visibly do nothing and wipe the redo stack.
  if (patches.length === 0) {
    return newState;
  }

  return produce(newState, (draft) => {
    draft.history.past.push({ patches, inversePatches });
    draft.history.future = [];
  });
};

const updateGroupConfiguration = ({
  draft,
  type,
  groupId,
  redactionConfig,
}: {
  draft: Draft<WorkerState>;
  type: OBJECT_TYPE;
  groupId: string;
  redactionConfig?: RedactionConfig;
}) => {
  if (type === 'udr') {
    const group = draft.udrAsset.boundingPolys[groupId];
    if (group) {
      group.redactionConfig = redactionConfig;
      group.version = (group.version ?? 0) + 1;
    }
    return;
  } else {
    const cluster = draft.groupMap[groupId];
    if (!cluster) {
      return;
    }

    cluster.redactionConfig = redactionConfig;
    for (const subsegmentId of cluster.subsegmentIds) {
      for (const objectId of original(draft)?.subsegmentMap[subsegmentId]
        ?.objectIds || []) {
        const res = draft.objectMap[objectId];
        if (res) {
          res.redactionConfig = redactionConfig;
          res.version = (res.version ?? 0) + 1;
        }
      }
    }
  }
};

export const addBackgroundBlurEdits = (
  draft: Draft<WorkerState>,
  udrGroupIds: string[],
  detectionGroupIds: string[],
  backgroundBlurLevel: BlurLevel
) => {
  let backgroundBlurEdits: BackgroundBlurSlice[] = [];

  // ------- add background blur for new detection groups

  // add span create new background blur for each detection segment
  detectionGroupIds.forEach((groupId) => {
    const clusterGroup = selectDetectionClusterGroupsSubset([groupId])({
      [namespace]: draft,
    })[groupId];

    clusterGroup?.segments.forEach((segment) => {
      // only add span if the segment is selected
      if (draft.selectedPolyGroups[segment.id]) {
        const groupSlice: BackgroundBlurSlice = [
          segment.startTimeMs,
          segment.stopTimeMs,
          { blurLevel: backgroundBlurLevel },
        ];

        backgroundBlurEdits = insertEditSlice(
          backgroundBlurEdits,
          groupSlice,
          draft.tdo?.details.veritoneFile?.videoFrameRate
        );
      }
    });
  });

  // ------- add background blur for udr groups
  udrGroupIds.forEach((groupId) => {
    const group = draft.udrAsset.boundingPolys[groupId];
    if (group && group.series.length > 0) {
      // do individual segments
      const segments = getUDRGroupIntervalBasedSegments(
        group.series,
        group.groupId
      );

      segments.forEach((segment) => {
        let someSubsegmentsSelected = false;
        for (const subsegmentId of segment.subsegmentIds) {
          // check if subsegments are selected
          if (draft.selectedPolyGroups[subsegmentId]) {
            someSubsegmentsSelected = true;
            break;
          }
        }
        if (someSubsegmentsSelected) {
          // or full span of group
          const segmentSlice: BackgroundBlurSlice = [
            segment.startTimeMs,
            segment.stopTimeMs,
            { blurLevel: backgroundBlurLevel },
          ];

          backgroundBlurEdits = insertEditSlice(
            backgroundBlurEdits,
            segmentSlice,
            draft.tdo?.details.veritoneFile?.videoFrameRate
          );
        }
      });
    }
  });

  //  -------  add existing edits (priority)
  draft.backgroundBlurEdits.forEach(
    (existingSlice) =>
      (backgroundBlurEdits = insertEditSlice(
        backgroundBlurEdits,
        existingSlice,
        draft.tdo?.details.veritoneFile?.videoFrameRate
      ))
  );

  // replace
  draft.backgroundBlurEdits = backgroundBlurEdits;
};

const onSetIndividualRedaction = (
  state: Draft<WorkerState>,
  { payload }: { payload: ChangeRedactionRequest }
) => {
  const { redactionConfig, udrGroupIds, detectionGroups } = payload;

  const [nextState, patches, inversePatches] = produceWithPatches(
    state,
    (draft) => {
      detectionGroups.forEach(({ id, type }) =>
        updateGroupConfiguration({
          draft,
          type,
          groupId: id,
          redactionConfig,
        })
      );
      udrGroupIds.forEach((id) =>
        updateGroupConfiguration({
          draft,
          type: 'udr',
          groupId: id,
          redactionConfig,
        })
      );

      const backgroundBlurLevel = redactionConfig?.addBackgroundBlur;
      const detectionGroupIds = detectionGroups.map((g) => g.id);

      if (backgroundBlurLevel) {
        addBackgroundBlurEdits(
          draft,
          udrGroupIds,
          detectionGroupIds,
          backgroundBlurLevel
        );
      }
    }
  );

  return produce(nextState, (draft) => {
    draft.history.past.push({ patches, inversePatches });
    draft.history.future = [];
  });
};

const onBulkAddBackgroundBlur = (
  state: WorkerState,
  {
    payload,
  }: {
    payload: BulkAddBackgroundBlurRequest;
  }
) => {
  const { detectionType: detectionTypeRaw, blurLevel } = payload;

  const [nextState, patches, inversePatches] = produceWithPatches(
    state,
    (draft) => {
      const detectionGroupIds: string[] = [];
      let udrGroupIds: string[] = [];

      // TODO need to cleanup weird type conversions between licensePlate and plate
      const detectionType =
        detectionTypeRaw === 'plate' ? 'licensePlate' : detectionTypeRaw;

      if (detectionType === 'udr') {
        udrGroupIds = Object.keys(state.udrAsset.boundingPolys);
      } else {
        // update groupMap as well
        Object.entries(state.groupMap).forEach(([groupId, groupMapItem]) => {
          if (groupMapItem.type === detectionType) {
            detectionGroupIds.push(groupId);
          }
        });
      }

      if (blurLevel) {
        addBackgroundBlurEdits(
          draft,
          udrGroupIds,
          detectionGroupIds,
          blurLevel
        );
      }
    }
  );

  return produce(nextState, (draft) => {
    draft.history.past.push({ patches, inversePatches });
    draft.history.future = [];
  });
};

const onSetDetectionTypeRedaction = (
  state: WorkerState,
  {
    payload,
  }: {
    payload: BulkRedactRequest;
  }
) => {
  const { detectionType: detectionTypeRaw, redactionConfig } = payload;

  const [nextState, patches, inversePatches] = produceWithPatches(
    state,
    (draft) => {
      const detectionGroupIds: string[] = [];
      let udrGroupIds: string[] = [];

      // TODO need to cleanup weird type conversions between licensePlate and plate
      const detectionType =
        detectionTypeRaw === 'plate' ? 'licensePlate' : detectionTypeRaw;

      if (detectionType === 'udr') {
        const boundingPolys = Object.entries(state.udrAsset.boundingPolys).map(
          (udr) =>
            udr[1]
              ? [
                  udr[0],
                  Object.assign({}, udr[1], {
                    redactionConfig: redactionConfig,
                    version: (udr[1]?.version ?? 0) + 1,
                  }),
                ]
              : udr
        );

        // TODO: Use a typed from entries
        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
        draft.udrAsset.boundingPolys = Object.fromEntries(boundingPolys);

        // if inverse blur might need to add all background blur

        udrGroupIds = Object.keys(state.udrAsset.boundingPolys);
      } else {
        // update objectMap
        const objectMap = Object.entries(state.objectMap).map(
          ([objectId, objectMapItem]) => {
            if (objectMapItem.object.type === detectionType) {
              return objectMapItem
                ? [
                    objectId,
                    Object.assign({}, objectMapItem, {
                      redactionConfig: redactionConfig,
                      version: (objectMapItem?.version ?? 0) + 1,
                    }),
                  ]
                : [objectId, objectMapItem];
            }
            return [objectId, objectMapItem];
          }
        );

        Object.assign(draft.objectMap, Object.fromEntries(objectMap));

        // update groupMap as well
        const groupMap = Object.entries(state.groupMap).map(
          ([groupId, groupMapItem]) => {
            if (groupMapItem.type === detectionType) {
              detectionGroupIds.push(groupId);
              return groupMapItem
                ? [
                    groupId,
                    Object.assign({}, groupMapItem, {
                      redactionConfig: redactionConfig,
                      version: (groupMapItem?.version ?? 0) + 1,
                    }),
                  ]
                : [groupId, groupMapItem];
            }
            return [groupId, groupMapItem];
          }
        );

        Object.assign(draft.groupMap, Object.fromEntries(groupMap));
      }

      const backgroundBlurLevel = redactionConfig?.addBackgroundBlur;

      if (backgroundBlurLevel) {
        addBackgroundBlurEdits(
          draft,
          udrGroupIds,
          detectionGroupIds,
          backgroundBlurLevel
        );
      }
    }
  );

  return produce(nextState, (draft) => {
    draft.history.past.push({ patches, inversePatches });
    draft.history.future = [];
  });
};

const onSetEmptyUndo = (state: Draft<WorkerState>) =>
  produce(state, (draft) => {
    draft.history.past.push(null);
    draft.history.future = [];
  });

const onInSetBackgroundBlurEdits: Re<SetBackgroundBlurEditsRequest> = (
  state,
  { payload: { backgroundBlurEdits } }
) => {
  const [nextState, patches, inversePatches] = produceWithPatches(
    state,
    (draft) => {
      draft.backgroundBlurEdits = backgroundBlurEdits;
    }
  );

  return produce(nextState, (draft) => {
    draft.history.past.push({ patches, inversePatches });
    draft.history.future = [];
  });
};

export default createReducer(defaultState, (builder) => {
  builder
    .addCase(IN_SET_GLOBAL_SETTINGS, onSetGlobalSettings)
    .addCase(SET_SELECTED_GROUPS, onSetSelectedGroups)
    .addCase(REPLACE_SELECTED_GROUPS, onReplaceSelectedGroups)
    .addCase(REPLACE_CLUSTER_MAP, onReplaceClusterGroups)
    .addCase(PURGE_DETECTIONS, onPurgeDetections)
    .addCase(CHANGE_CLUSTER_LABEL, onSetChangedClusterLabels)
    .addCase(CHANGE_CLUSTER_SEGMENT_SORT_TYPE, onSetChangedSegmentSortType)
    .addCase(CHANGE_CLUSTER_PINNED, onSetChangedClusterPinned)
    .addCase(UPDATE_CLUSTER_ASSIGNMENT, onUpdateClusterAssignment)
    .addCase(SET_MERGE_SELECT_CLUSTER_IDS, onSetEmptyUndo)
    .addCase(SET_MERGE_GROUP_ID, onSetEmptyUndo)
    .addCase(SET_CLUSTER_MERGE_SEGMENTS, onSetEmptyUndo)
    .addCase(DELETE_CLUSTER, onDeleteCluster)
    .addCase(UPDATE_CURRENT_TIME_MEDIA_PLAYER, onUpdateCurrentTime)
    .addCase(IN_LOAD_MEDIA_DATA, onInLoadMedia)
    .addCase(IN_CLEAR_MEDIA_DATA, onClearMediaData)
    .addCase(OUT_FACE_DETECTION_READY, onFaceDetectionReady)
    .addCase(OUT_INTERPOLATION_UPDATE, onOutInterpolationUpdate)
    .addCase(IN_CHANGE_FILTER_PARAMETERS, onSetFilterParameters)
    .addCase(CHANGE_SHAPE, onChangeShape)
    .addCase(CHANGE_CODE, onChangeCode)
    .addCase(CHANGE_TEXT_OVERLAY, onChangeTextOverlay)
    .addCase(SET_DETECTION_TYPE_REDACTION, onSetDetectionTypeRedaction)
    .addCase(BULK_ADD_BACKGROUND_BLUR, onBulkAddBackgroundBlur)
    .addCase(SET_INDIVIDUAL_REDACTION, onSetIndividualRedaction)
    .addCase(IN_SET_BACKGROUND_BLUR_EDITS, onInSetBackgroundBlurEdits);
});
